import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import { GetTopicsCourseType } from "@packages/api/madlenAPI.schemas";
import { firstTopicOfCourse } from "@packages/utils";
import { UnitTopicSelector } from "@packages/components/unit-topic-selector";
import ErrorComponent from "../error-component";
import GradeSelector from "../grade-selector";
import { Button } from "@core/index";
import { useToast } from "@packages/contexts/ToastContext";
import LoadingContainer from "@packages/components/Loading";
import CourseAndAudianceInput, {
  processPrompt,
} from "./CourseAndAudianceInput";
import { UnitOutcomeSelector } from "@features/assignments/AssignmentGenerator/components/unit-outcome-selector";
import { FileUploader } from "@core/file-uploader";
import { TeacherDocumentResponseDto } from "@packages/api/madlenAPI.schemas";
import CurriculumSelector, {
  CurriculumType,
  InternationalCurriculum,
} from "./CurriculumSelector";
import OutputLanguageSelector, { ThemeColor } from "./OutputLanguageSelector";
import {
  acceptedFileTypes,
  gradientClasses,
  buttonClasses,
  submitGradientClasses,
  labelClasses,
} from "./constants";

export interface CreateModalProps {
  title: string;
  themeColor: ThemeColor;
  backPath: string;
  onSubmit: (input: any) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
  initialInput: {
    courseName: string;
    gradeLevel: number;
    prompt: string;
    environment: string;
    isPublic: boolean;
    generationType: string;
    teacherDocumentIds?: number[];
    learningOutcomeIds?: number[];
    outputLanguage?: string;
  };
  loadingTitle: string;
  errorTitle: string;
  errorDescription: string;
  submitButtonText: string;
  placeholder?: string;
  after?: React.ReactNode;
  enableFileUpload?: boolean;
  isSuperTool?: boolean;
  hideBackButton?: boolean;
}

const CreateModal = ({
  title,
  themeColor,
  backPath,
  onSubmit,
  isLoading,
  error,
  initialInput,
  loadingTitle,
  errorTitle,
  errorDescription,
  submitButtonText,
  placeholder,
  after,
  enableFileUpload = false,
  isSuperTool = false,
  hideBackButton = false,
}: CreateModalProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [input, setInput] = useState({
    ...initialInput,
    learningOutcomeIds: initialInput.learningOutcomeIds || [],
  });

  const [isTopicSelection, setIsTopicSelection] = useState(false);
  const [isOutcomeSelection, setIsOutcomeSelection] = useState(
    initialInput.generationType === "LEARNING_OUTCOME" ||
      initialInput.generationType === "MAARIF_LEARNING_OUTCOME",
  );
  const [selectedTopics, setSelectedTopics] = useState<number[]>([]);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<
    TeacherDocumentResponseDto[]
  >([]);

  // New states for curriculum selection
  const [curriculumType, setCurriculumType] = useState<CurriculumType>("MEB");
  const [internationalCurriculum, setInternationalCurriculum] =
    useState<InternationalCurriculum>("AP");

  // Output language state
  const [outputLanguage, setOutputLanguage] = useState(
    initialInput.outputLanguage || i18n.language,
  );

  const location = useLocation();

  // Ensure grade level is treated as a number for calculations
  const gradeAsNumber = Number(input.gradeLevel);

  // Check if selected grade is in 1-4 range
  const isJuniorGrade = gradeAsNumber >= -1 && gradeAsNumber <= 4;

  const [isMaarif, setIsMaarif] = useState(() => {
    // İlk olarak initialInput'tan kontrol et
    if (initialInput.generationType === "MAARIF_LEARNING_OUTCOME") {
      return true;
    }

    // Sonra localStorage'dan kontrol et
    const savedValue = localStorage.getItem("isMaarifEnabled");
    if (savedValue) {
      return JSON.parse(savedValue);
    }

    // Son olarak location.state'den kontrol et
    return location.state?.isMaarif ?? false;
  });

  // Maarif durumu localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem("isMaarifEnabled", JSON.stringify(isMaarif));
  }, [isMaarif]);

  const placeholderTopic = useMemo(() => {
    return firstTopicOfCourse(
      Number(input.gradeLevel),
      input.courseName as GetTopicsCourseType,
    );
  }, [input.gradeLevel, input.courseName]);

  const handleCurriculumTypeChange = (type: CurriculumType) => {
    setCurriculumType(type);

    // Reset course selection when changing curriculum type
    const availableCourses = Object.entries(CourseData).filter(
      ([key, value]) => {
        if (type === "MEB") {
          return value.visible(gradeAsNumber) && isTurkishCourse(key);
        } else {
          return value.visible(gradeAsNumber) && !isTurkishCourse(key);
        }
      },
    );

    const firstAvailableCourse =
      availableCourses[0]?.[0] as GetTopicsCourseType;

    // Handle selection state changes based on curriculum type
    if (type === "INTERNATIONAL") {
      // If outcomes are currently selected, switch to topic selection
      if (isOutcomeSelection) {
        setIsTopicSelection(true);
        setIsOutcomeSelection(false);
        setIsFileUpload(false);
        setInput({
          ...input,
          courseName: firstAvailableCourse || "UNKNOWN",
          generationType: "TOPIC_SELECTION",
          prompt: "",
          learningOutcomeIds: [],
        });
      } else {
        setInput({
          ...input,
          courseName: firstAvailableCourse || "UNKNOWN",
        });
      }
    } else {
      // When switching to national education, auto-select outcomes
      setIsTopicSelection(false);
      setIsOutcomeSelection(true);
      setIsFileUpload(false);
      setInput({
        ...input,
        courseName: firstAvailableCourse || "UNKNOWN",
        generationType:
          isMaarif || gradeAsNumber < 5
            ? "MAARIF_LEARNING_OUTCOME"
            : "LEARNING_OUTCOME",
        prompt: "",
        learningOutcomeIds: [],
      });
    }

    setSelectedTopics([]);
  };

  const handleInternationalCurriculumChange = (
    curriculum: InternationalCurriculum,
  ) => {
    setInternationalCurriculum(curriculum);

    // Reset course selection when changing international curriculum
    const availableCourses = Object.entries(CourseData).filter(
      ([key, value]) => {
        return value.visible(gradeAsNumber) && key.startsWith(`${curriculum}_`);
      },
    );

    const firstAvailableCourse =
      availableCourses[0]?.[0] as GetTopicsCourseType;
    setInput({
      ...input,
      courseName: firstAvailableCourse || "UNKNOWN",
    });
    setSelectedTopics([]);
  };

  const handleSubmit = async () => {
    // Standard validations
    if (isTopicSelection && selectedTopics.length === 0) {
      addToast({
        title: t("notifications.assignment.emptyTopics"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    if (
      isOutcomeSelection &&
      gradeAsNumber !== -1 &&
      (!input.learningOutcomeIds || input.learningOutcomeIds.length === 0)
    ) {
      addToast({
        title: t("notifications.assignment.emptyOutcomes"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    if (
      !isTopicSelection &&
      !isFileUpload &&
      !isOutcomeSelection &&
      !input.prompt.trim()
    ) {
      addToast({
        title: t("notifications.assignment.emptyPrompt"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    if (enableFileUpload && isFileUpload && uploadedFiles.length === 0) {
      addToast({
        duration: 5000,
        title: t("notifications.assignment.emptyDocument"),
        variant: "error",
      });
      return;
    }

    // Prepare submission data
    let submissionData = {
      ...input,
      generationType: isOutcomeSelection
        ? isMaarif || gradeAsNumber < 5
          ? "MAARIF_LEARNING_OUTCOME"
          : "LEARNING_OUTCOME"
        : input.generationType,
      contentLanguage: outputLanguage,
    };

    if (enableFileUpload && isFileUpload) {
      submissionData.teacherDocumentIds = uploadedFiles
        .map((file) => file.id)
        .filter((id): id is number => id !== undefined);
    }

    await onSubmit(submissionData);
  };

  const handleFileUploadSuccess = (
    url: string,
    response?: TeacherDocumentResponseDto,
  ) => {
    if (!response) {
      addToast({
        title: t("assistant.uploadError"),
        description: t("assistant.uploadErrorDescription"),
        variant: "error",
        duration: 5000,
      });
      return;
    }

    setUploadedFiles((prevFiles) => [...prevFiles, response]);
  };

  const handleFileUploadError = (error: string) => {
    addToast({
      title: t("assistant.uploadError"),
      description: error,
      variant: "error",
      duration: 5000,
    });
  };

  const handleGradeChange = (grade: number) => {
    const availableCourses = Object.entries(CourseData).filter(([_, value]) =>
      value.visible(grade),
    );
    const firstAvailableCourse =
      availableCourses[0]?.[0] as GetTopicsCourseType;
    const doesPreviousCourseExist = availableCourses.some(
      ([key]) => key === input.courseName,
    );
    setSelectedTopics([]);

    const isNewGradeJunior = (grade >= 1 && grade <= 4) || grade === -1;
    if (isNewGradeJunior || !firstAvailableCourse) {
      setIsTopicSelection(false);
      setIsOutcomeSelection(false);
      setIsFileUpload(false);
    } else {
      setIsOutcomeSelection((prev) => {
        return prev && !!firstAvailableCourse;
      });
    }

    setInput({
      ...input,
      gradeLevel: grade,
      courseName: doesPreviousCourseExist
        ? input.courseName
        : (firstAvailableCourse ?? "UNKNOWN"),
      generationType:
        isNewGradeJunior || !firstAvailableCourse
          ? "PROMPT"
          : input.generationType,
      learningOutcomeIds: [], // Reset learning outcomes when grade changes
    });
  };

  useEffect(() => {
    const availableCourses = Object.entries(CourseData).filter(([_, value]) =>
      value.visible(gradeAsNumber),
    );
    const doesPreviousCourseExist = availableCourses.some(
      ([key]) => key === input.courseName,
    );
    const firstAvailableCourse =
      availableCourses[0]?.[0] as GetTopicsCourseType;

    setInput((prev) => {
      return {
        ...prev,
        courseName: doesPreviousCourseExist
          ? input.courseName
          : (firstAvailableCourse ?? "UNKNOWN"),
        prompt: "",
        learningOutcomeIds: [], // Reset learning outcomes on grade/language change
      };
    });
    setSelectedTopics([]);
    setIsOutcomeSelection((prev) => {
      return prev && gradeAsNumber !== -1;
    });
  }, [i18n.language, gradeAsNumber]);

  if (isLoading) {
    return <LoadingContainer title={loadingTitle} themeColor={themeColor} />;
  }

  if (error) {
    return <ErrorComponent title={errorTitle} description={errorDescription} />;
  }

  const shouldShowMaarifToggle =
    gradeAsNumber !== -1 &&
    gradeAsNumber > 4 &&
    !isSuperTool &&
    curriculumType === "MEB";

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-gray-50 to-${themeColor}-50 dark:from-gray-900 dark:to-${themeColor}-900`}
    >
      <div className="container max-w-7xl mx-auto px-4 py-4 relative">
        {!isSuperTool && !hideBackButton && (
          <Button
            key={backPath}
            onClick={() => navigate(backPath)}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor={buttonClasses[themeColor]}
            rounded="lg"
            className="mb-2"
          >
            <ArrowLeft size={24} />
          </Button>
        )}

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-200/50 dark:border-gray-700/50">
            <div className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2 flex flex-col items-start">
                  <h4
                    className={`inline-block text-base font-medium ${labelClasses[themeColor]} text-center w-full`}
                  >
                    {title}
                  </h4>
                  <div
                    className={`w-full grid grid-cols-1 ${isSuperTool ? "" : "md:grid-cols-2"} gap-3`}
                  >
                    <div className="grid grid-cols-4 gap-3">
                      <GradeSelector
                        selectedGrade={Number(input.gradeLevel)}
                        setSelectedGrade={handleGradeChange}
                      />
                    </div>
                    {!isSuperTool && (
                      <div className="flex flex-col items-center gap-4 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 backdrop-blur-sm">
                        <div
                          className={`w-full flex flex-col gap-4 justify-center align-center h-full`}
                        >
                          <div className="flex flex-col">
                            <label
                              className={`text-sm font-medium ${labelClasses[themeColor]} mb-2 text-center`}
                            >
                              {t(
                                "hooks.form.outputLanguage",
                                "Output Language",
                              )}
                            </label>
                            <div className="flex justify-center">
                              <OutputLanguageSelector
                                selectedLanguage={outputLanguage}
                                onLanguageChange={setOutputLanguage}
                                themeColor={themeColor}
                                gradientClasses={gradientClasses}
                              />
                            </div>
                          </div>

                          {shouldShowMaarifToggle && (
                            <div className="flex flex-col items-center justify-center p-3 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                              <span className="block w-full text-sm font-medium text-gray-900 dark:text-gray-300 text-center mb-2">
                                Türkiye Yüzyılı Maarif Modeli
                              </span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isMaarif}
                                  onChange={(e) => {
                                    setIsMaarif(e.target.checked);
                                  }}
                                  className="sr-only peer"
                                />
                                <div
                                  className={`w-11 h-6 rounded-full peer relative transition-colors duration-300
                                ${isMaarif ? "bg-blue-600 dark:bg-blue-600" : "bg-gray-200 dark:bg-gray-700"}
                                peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800`}
                                ></div>
                                <div
                                  className={`absolute h-5 w-5 rounded-full bg-white border border-gray-300 
                                transition-transform duration-300 top-[2px] start-[2px]
                                ${isMaarif ? "translate-x-5 rtl:-translate-x-5" : ""}`}
                                ></div>
                                <span className="ms-3 text-xs text-gray-600 dark:text-gray-400">
                                  {isMaarif ? "Açık" : "Kapalı"}
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {gradeAsNumber !== -1 && (
                  <CurriculumSelector
                    gradeLevel={gradeAsNumber}
                    themeColor={themeColor}
                    gradientClasses={gradientClasses}
                    labelClasses={labelClasses}
                    curriculumType={curriculumType}
                    setCurriculumType={handleCurriculumTypeChange}
                    internationalCurriculum={internationalCurriculum}
                    setInternationalCurriculum={
                      handleInternationalCurriculumChange
                    }
                    selectedCourse={input.courseName as GetTopicsCourseType}
                    setSelectedCourse={(course) => {
                      setSelectedTopics([]);
                      setInput({
                        ...input,
                        courseName: course,
                      });
                    }}
                    isMaarif={isMaarif}
                  />
                )}

                {gradeAsNumber === -1 && (
                  <CourseAndAudianceInput
                    input={input}
                    setInput={setInput}
                    labelClass={labelClasses[themeColor]}
                  />
                )}
              </div>

              <div className="space-y-4">
                {gradeAsNumber !== -1 && (
                  <div
                    className={`flex flex-col md:flex-row flex-wrap justify-center gap-4 ${
                      !isJuniorGrade && enableFileUpload
                        ? "grid grid-cols-2 gap-4"
                        : ""
                    }`}
                  >
                    <Button
                      type="button"
                      className="flex-1"
                      onClick={() => {
                        setIsTopicSelection(false);
                        setIsOutcomeSelection(false);
                        setIsFileUpload(false);
                        setInput({
                          ...input,
                          generationType: "PROMPT",
                          prompt: "", // Reset lesson topic when switching modes
                          learningOutcomeIds: [], // Reset learning outcomes
                        });
                        setSelectedTopics([]); // Reset selected topics
                        setUploadedFiles([]);
                      }}
                      variant="solid"
                      size="md"
                      rounded="lg"
                      textColor={
                        !isTopicSelection &&
                        !isOutcomeSelection &&
                        !isFileUpload
                          ? "text-white"
                          : "text-gray-600 dark:text-gray-400"
                      }
                      bgColor={
                        !isTopicSelection &&
                        !isOutcomeSelection &&
                        !isFileUpload
                          ? `bg-gradient-to-r ${gradientClasses[themeColor]} shadow-lg`
                          : "bg-gray-100 dark:bg-gray-700"
                      }
                    >
                      {t("common.freeInput")}
                    </Button>

                    {!isJuniorGrade ? (
                      <Button
                        type="button"
                        className="flex-1"
                        onClick={() => {
                          setIsTopicSelection(true);
                          setIsOutcomeSelection(false);
                          setIsFileUpload(false);
                          setInput({
                            ...input,
                            generationType: "TOPIC_SELECTION",
                            prompt: "",
                            teacherDocumentIds: [],
                            learningOutcomeIds: [],
                          });
                          setUploadedFiles([]);
                        }}
                        variant="solid"
                        size="md"
                        rounded="lg"
                        textColor={
                          isTopicSelection
                            ? "text-white"
                            : "text-gray-600 dark:text-gray-400"
                        }
                        bgColor={
                          isTopicSelection
                            ? `bg-gradient-to-r ${gradientClasses[themeColor]} shadow-lg`
                            : "bg-gray-100 dark:bg-gray-700"
                        }
                      >
                        {t("common.topicSelection")}
                      </Button>
                    ) : null}

                    {/* Hide outcomes button for international curriculum */}
                    {curriculumType === "MEB" && (
                      <Button
                        type="button"
                        className={isJuniorGrade ? "flex-1" : ""}
                        onClick={() => {
                          setIsTopicSelection(false);
                          setIsOutcomeSelection(true);
                          setIsFileUpload(false);
                          const availableCourses = Object.entries(
                            CourseData,
                          ).filter(
                            ([, value]) =>
                              value.visible(gradeAsNumber) &&
                              (isMaarif || gradeAsNumber < 5
                                ? value.hasMaarifOutcomes
                                : value.hasOutcomes),
                          );
                          const firstAvailableCourse =
                            availableCourses[0]?.[0] as GetTopicsCourseType;
                          const doesPreviousCourseExist = availableCourses.some(
                            ([key]) => key === input.courseName,
                          );
                          setInput({
                            ...input,
                            generationType:
                              isMaarif || gradeAsNumber < 5
                                ? "MAARIF_LEARNING_OUTCOME"
                                : "LEARNING_OUTCOME",
                            prompt: "",
                            teacherDocumentIds: [],
                            learningOutcomeIds: [],
                            courseName: doesPreviousCourseExist
                              ? input.courseName
                              : (firstAvailableCourse ?? "UNKNOWN"),
                          });
                          setSelectedTopics([]);
                        }}
                        variant="solid"
                        size="md"
                        rounded="lg"
                        textColor={
                          isOutcomeSelection
                            ? "text-white"
                            : "text-gray-600 dark:text-gray-400"
                        }
                        bgColor={
                          isOutcomeSelection
                            ? `bg-gradient-to-r ${gradientClasses[themeColor]} shadow-lg`
                            : "bg-gray-100 dark:bg-gray-700"
                        }
                      >
                        {isMaarif || gradeAsNumber < 5
                          ? t("common.maarifOutcomeSelection")
                          : t("common.outcomeSelection")}
                      </Button>
                    )}

                    {enableFileUpload && (
                      <Button
                        type="button"
                        className={"flex-1"}
                        onClick={() => {
                          setIsTopicSelection(false);
                          setIsOutcomeSelection(false);
                          setIsFileUpload(true);
                          setInput({
                            ...input,
                            generationType: "TEACHER_DOCUMENT",
                            prompt: "",
                            learningOutcomeIds: [],
                          });
                          setSelectedTopics([]);
                        }}
                        variant="solid"
                        size="md"
                        rounded="lg"
                        textColor={
                          isFileUpload
                            ? "text-white"
                            : "text-gray-600 dark:text-gray-400"
                        }
                        bgColor={
                          isFileUpload
                            ? `bg-gradient-to-r ${gradientClasses[themeColor]} shadow-lg`
                            : "bg-gray-100 dark:bg-gray-700"
                        }
                      >
                        {t("hooks.form.pdfInput")}
                      </Button>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  {isTopicSelection && !isJuniorGrade ? (
                    <UnitTopicSelector
                      grade={Number(input.gradeLevel)}
                      courseType={input.courseName as GetTopicsCourseType}
                      selectedTopics={selectedTopics}
                      setSelectedTopics={(topicIds, topicNames) => {
                        setSelectedTopics(topicIds);
                        setInput({
                          ...input,
                          prompt: topicNames ? topicNames.join(", ") : "",
                        });
                      }}
                    />
                  ) : isOutcomeSelection && gradeAsNumber !== -1 ? (
                    <UnitOutcomeSelector
                      grade={Number(input.gradeLevel)}
                      courseType={input.courseName as GetTopicsCourseType}
                      selectedOutcomes={input.learningOutcomeIds || []}
                      isMaarif={isMaarif || gradeAsNumber < 5}
                      setSelectedOutcomes={(outcomeIds) => {
                        setInput({
                          ...input,
                          learningOutcomeIds: outcomeIds,
                          generationType:
                            isMaarif || gradeAsNumber < 5
                              ? "MAARIF_LEARNING_OUTCOME"
                              : "LEARNING_OUTCOME",
                        });
                      }}
                    />
                  ) : enableFileUpload && isFileUpload ? (
                    <div className="mb-6">
                      <h3
                        className={`text-lg font-medium mb-2 text-transparent bg-clip-text bg-gradient-to-r ${gradientClasses[themeColor]}`}
                      >
                        {t("textLeveler.create.uploadDocumentTitle")}
                      </h3>
                      <FileUploader
                        accept={acceptedFileTypes}
                        onUploadSuccess={handleFileUploadSuccess}
                        onFileDelete={() => setUploadedFiles([])}
                        onUploadError={handleFileUploadError}
                        maxSize={100 * 1024 * 1024} // 100MB
                        dragActiveText={t("textLeveler.create.dropDocument")}
                        dragInactiveText={t(
                          "textLeveler.create.uploadDocument",
                        )}
                        uploadColor={
                          themeColor === "cyan"
                            ? "blue"
                            : themeColor === "red" ||
                                themeColor === "blue" ||
                                themeColor === "green"
                              ? themeColor
                              : "blue"
                        }
                        extractColor={
                          themeColor === "cyan"
                            ? "blue"
                            : themeColor === "red" ||
                                themeColor === "blue" ||
                                themeColor === "green"
                              ? themeColor
                              : "blue"
                        }
                        successColor="green"
                        shouldExtractContent
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label
                        className={`inline-block text-sm font-medium ${labelClasses[themeColor]}`}
                      >
                        {isJuniorGrade || gradeAsNumber === -1
                          ? t("hooks.form.promptJunior")
                          : t("hooks.form.prompt")}
                      </label>
                      <input
                        type="text"
                        data-testid="lesson-topic-input"
                        value={gradeAsNumber === -1 ? undefined : input.prompt}
                        onChange={(e) => {
                          if (gradeAsNumber === -1) {
                            const newPrompt = processPrompt(
                              e.target.value,
                              input.prompt,
                              i18n.language,
                            );
                            setInput({ ...input, prompt: newPrompt });
                          } else {
                            setInput({ ...input, prompt: e.target.value });
                          }
                        }}
                        className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
                        placeholder={
                          isJuniorGrade || gradeAsNumber === -1
                            ? t("hooks.form.promptJuniorPlaceholder")
                            : placeholder ||
                              (i18n.language === "tr"
                                ? "Örnek: "
                                : i18n.language === "en"
                                  ? "Example: "
                                  : "Beispiel: ") + placeholderTopic?.topic
                        }
                      />
                    </div>
                  )}
                </div>
              </div>

              {after}

              <div className="flex flex-col md:flex-row items-stretch gap-4">
                <Button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  variant="solid"
                  size="lg"
                  rounded="xl"
                  textColor="text-white"
                  bgColor={`bg-gradient-to-r ${submitGradientClasses[themeColor]}`}
                  fullWidth
                  className={`my-1 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-${themeColor}-500/50`}
                  data-testid="toolbox-create-button"
                >
                  {submitButtonText}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateModal;
